import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import Analytics, { OptimisationInputEvent } from '@ecs-uk/web-common-analytics';

import { isObjectEmpty } from '../../helpers';
import { DownloadIcon } from '../Icons/DownloadIcon';
import BlueTick from './blueTick';
import tooltipForBenefit from './tooltipForBenefit';
import { FreeTrialText } from './FreeProduct/FreeProductPanel.styles';
import {
  CardBenefits,
  CardContent,
  CardContainer,
  CurrentSubscriptionHeader,
  PricePoint,
  ProductFactSheet,
} from './productPanel.styles';
import { abTests, products } from '../../constants';
import BenefitsBanner from './BenefitsBanner/benefitsBanner';
import useExperienceApi from '../../hooks/useExperienceApi';
import { StyledHeader } from './productOptionsCards.styles';

const CurrentSubscriptionCard = ({ freeTrial, switchPermitted, currentSubscription }) => {
  const { targetedFeatures, conductricsDecisions } = useExperienceApi();

  const conductrics = useSelector(state => state?.conductrics);
  const variant = conductrics?.sels?.[abTests.PRICE_PRODUCT_FACT_SHEET_SWAP.apiCode];
  const hasCurrentSubscriptionPlanCode = Boolean(currentSubscription?.planCode);

  const userIsInFreeTrial = !isObjectEmpty(freeTrial) && !!freeTrial.remaining;

  // Filter out PROTECT_HOOK from BenefitsBanner - it's handled by RetentionDownsellBanner
  const benefitsFeatures = targetedFeatures.filter(f => f.agentReport?.id !== abTests.PRODMOVE_PROTECT_HOOK.apiCode);
  const benefitsDecisions = conductricsDecisions.filter(d => d.id !== abTests.PRODMOVE_PROTECT_HOOK.apiCode);
  const showBenefitsBanner = benefitsFeatures.length > 0 || benefitsDecisions.length > 0;

  const isDeferred = !!currentSubscription?.defermentInfo;

  const doNotShowPrice = isDeferred && currentSubscription.family === 'credit_expert';

  const isBreachOrBreachPlus = [products.breach, products.breachPlus].includes(currentSubscription.family);

  useEffect(() => {
    if (variant === 'A' || variant === 'B') {
      Analytics.publishOnce(
        OptimisationInputEvent.fromObject({
          testName: abTests.PRICE_PRODUCT_FACT_SHEET_SWAP.name,
          testArea: 'Product Movement',
          testVariant: variant,
        })
      );
    }
  }, [variant]);

  const isVariantB = variant === 'B';

  const getFreeTrialText = () => {
    let identityText = '';
    if (currentSubscription.family === products.identity && !hasCurrentSubscriptionPlanCode) {
      identityText = 'with complimentary CreditExpert features ';
    }
    return `You have a free ${freeTrial.remaining.value} ${freeTrial.remaining.unit} trial ${identityText}until ${freeTrial.ends}`;
  };

  return (
    <CardContainer
      gridLocation="main"
      isCurrentProduct
      {...setAutomationElement(`panel-${currentSubscription.family}`)}
    >
      <CurrentSubscriptionHeader priceSheetSwap={isVariantB}>
        <p>Your current subscription</p>
        <h2>{currentSubscription.titleText}</h2>
        {!doNotShowPrice && !isVariantB && (
          <PricePointContainer currentSubscription={currentSubscription} priceStyleSwap={isVariantB} />
        )}
        {isVariantB && (
          <ProductFactSheetContainer
            isVariantB={isVariantB}
            isBreachOrBreachPlus={isBreachOrBreachPlus}
            currentSubscription={currentSubscription}
          />
        )}
      </CurrentSubscriptionHeader>
      {currentSubscription.family === products.credit_expert && showBenefitsBanner && <BenefitsBanner />}
      <CardContent>
        <CardBenefits>
          <h3>Benefits include:</h3>
          <ul>
            {currentSubscription.benefitCopy.map(txt => (
              <li key={txt}>
                <BlueTick />
                {txt}
                {tooltipForBenefit(txt)}
              </li>
            ))}
          </ul>
        </CardBenefits>
      </CardContent>
      {userIsInFreeTrial && switchPermitted && (
        <FreeTrialText {...setAutomationElement('free-trial-text')}>{getFreeTrialText()}</FreeTrialText>
      )}
      {!isVariantB ? (
        <ProductFactSheetContainer
          isVariantB={!isVariantB}
          isBreachOrBreachPlus={isBreachOrBreachPlus}
          currentSubscription={currentSubscription}
        />
      ) : (
        <PricePointContainer currentSubscription={currentSubscription} priceStyleSwap={isVariantB} />
      )}
    </CardContainer>
  );
};

const ProductFactSheetContainer = ({ isBreachOrBreachPlus, currentSubscription, isVariantB }) => (
  <ProductFactSheet isBreachOrBreachPlus={isBreachOrBreachPlus} priceSheetSwap={isVariantB}>
    <DownloadIcon />
    <a
      {...setAutomationElement(`${currentSubscription?.family}-panel-product-factsheet-link`)}
      href={currentSubscription?.copyLink}
      target="_blank"
      rel="noreferrer"
    >
      View {currentSubscription?.productName} product fact sheet
    </a>
  </ProductFactSheet>
);

const PricePointContainer = ({ currentSubscription, priceStyleSwap }) => (
  <React.Fragment>
    <StyledHeader />
    <PricePoint
      priceSheetSwap={priceStyleSwap}
      {...setAutomationElement(`${currentSubscription?.family}-price-summary`)}
      color={priceStyleSwap ? '#263B68' : 'white'}
    >
      {currentSubscription?.priceSummary}
    </PricePoint>
  </React.Fragment>
);

ProductFactSheetContainer.propTypes = {
  isBreachOrBreachPlus: PropTypes.bool.isRequired,
  currentSubscription: PropTypes.shape({
    copyLink: PropTypes.string.isRequired,
    productName: PropTypes.string.isRequired,
    family: PropTypes.string.isRequired,
  }).isRequired,
  isVariantB: PropTypes.bool.isRequired,
};

PricePointContainer.propTypes = {
  currentSubscription: PropTypes.shape({
    priceSummary: PropTypes.string.isRequired,
    family: PropTypes.string.isRequired,
  }).isRequired,
  priceStyleSwap: PropTypes.bool.isRequired,
};

CurrentSubscriptionCard.propTypes = {
  freeTrial: PropTypes.shape({
    ends: PropTypes.string,
    remaining: PropTypes.shape({
      unit: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
  currentSubscription: PropTypes.shape({
    copyLink: PropTypes.string.isRequired,
    benefitCopy: PropTypes.arrayOf(PropTypes.string).isRequired,
    titleText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
    priceSummary: PropTypes.string.isRequired,
    productName: PropTypes.string.isRequired,
    family: PropTypes.string.isRequired,
    defermentInfo: PropTypes.shape({}),
    planCode: PropTypes.string,
  }).isRequired,
  switchPermitted: PropTypes.bool.isRequired,
  isComplimentary: PropTypes.bool,
};

CurrentSubscriptionCard.defaultProps = {
  freeTrial: {},
  isComplimentary: false,
};

export default CurrentSubscriptionCard;
